import { PagingParameters } from "@/api/dtos";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import {
  useDeleteComplexLink,
  useGetComplexRelationFields,
  useGetNotRelatedResource,
} from "@/api/related-fields";
import { bibliographyColumnsVisibility } from "@/config/dashboard/bibliography";
import { sculptureEditionsColumnsVisibility } from "@/config/dashboard/sculpture-editions";
import { FormLayoutElement } from "@/config/forms/general";
import { pickSculptureEditionTechniqueLine } from "@/lib/computeFields";
import i18n, { SupportedLocale } from "@/i18n";
import {
  Author,
  Bibliography,
  SculptureEdition,
  SculptureEditionInBibliography,
} from "@/lib/types";
import { ColumnDef, Row, VisibilityState } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LinkDrawerRowActions } from "@/components/table/LinkDrawerRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { LinksTableProps } from "./ArtworkInAuction";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";
import {
  BibliographyLinkGridCard,
  SculptureEditionLinkGridCard,
} from "./relatedLinkGridCards";
import { GridCardWithActions } from "./GridCardWithActions";
import SculptureEditionInBibliographyDrawer from "./drawer/SculptureEditionInBibliographyDrawer";

interface ComplexLinkProps {
  id: string;
  sculptureEdition?: SculptureEdition;
  bibliography?: Bibliography;
  fields: FormLayoutElement[] | undefined;
  type: LinkableType;
}

/**
 * SculptureEditionInBibliography component
 * @returns {JSX.Element}
 */
const SculptureEditionInBibliographyLinks = ({
  id,
  sculptureEdition,
  bibliography,
  fields,
  type,
}: ComplexLinkProps) => {
  const { t } = useTranslation();
  const { data: relatedData } = useGetComplexRelationFields(
    id,
    "sculptureEditionInBibliography",
    type,
  );

  const { mutate: deleteSculptureEditionInBibliography } = useDeleteComplexLink(
    id,
    "sculptureEditionInBibliography",
    type,
  );

  const [searchContent, setSearchContent] = useState("");

  const [isAddMode, setIsAddMode] = useState(false);

  const handleDeleteRelation = useCallback(
    (id: string) => {
      deleteSculptureEditionInBibliography({
        id,
        relation: "sculptureEditionInBibliography",
      });
    },
    [deleteSculptureEditionInBibliography],
  );

  const renderSculptureEditionInBibliographyRowActions = useCallback(
    (data: SculptureEditionInBibliography) => {
      const preview =
            type === "bibliography" && data.sculptureEdition
              ? {
                  previewType: "sculptureEdition" as const,
                  previewId: data.sculptureEdition.id,
                  previewElements: [
                    {
                      label: t("sculpture.form.sculptureEditionNumeroEnregistrement"),
                      value: data.sculptureEdition.sculptureEditionNumeroEnregistrement,
                      type: "text" as const,
                    },
                    {
                      label: t("sculpture.form.sculptureEditionNotes"),
                      value: data.sculptureEdition.sculptureEditionNotes ?? "",
                      type: "richtext" as const,
                    },
                    {
                      label: t("attachments"),
                      value: data.sculptureEdition.attachments ?? [],
                      type: "image" as const,
                    },
                  ],
                }
              : type === "sculptureEdition" && data.bibliography
                ? {
                    previewType: "bibliography" as const,
                    previewId: data.bibliography.id,
                    previewElements: [
                      {
                        label: t("bibliographieDescriptionNumeroBibliographie"),
                        value:
                          data.bibliography.bibliographieDescriptionNumeroBibliographie,
                        type: "text" as const,
                      },
                      {
                        label: t("bibliographieDescriptionResumeLong"),
                        value: data.bibliography.bibliographieDescriptionResumeLong,
                        type: "richtext" as const,
                      },
                      {
                        label: t("attachments"),
                        value: data.bibliography.attachments,
                        type: "image" as const,
                      },
                    ],
                  }
                : null;

          if (!preview) return null;

          return (
            <LinkDrawerRowActions
              previewType={preview.previewType}
              previewId={preview.previewId}
              previewElements={preview.previewElements}
              onDelete={() => handleDeleteRelation(data.id)}
              editDrawer={({ open, onOpenChange }) => (
                <SculptureEditionInBibliographyDrawer
                  key={data.id}
                  fields={fields}
                  data={data}
                  queryId={id}
                  type={type}
                  open={open}
                  onOpenChange={onOpenChange}
                />
              )}
            />
          );
    },
    [t, type, fields, handleDeleteRelation, id],
  );

  const columns: ColumnDef<SculptureEditionInBibliography, any>[] = useMemo(
    () => [
      ...(type === "bibliography"
        ? [
            {
              accessorKey: "vignette",
              header: "",
              size: 100,
              cell: ({ row }: { row: Row<SculptureEditionInBibliography> }) => (
                <ImageCell
                  value=""
                  attachments={row.original.sculptureEdition?.attachments ?? []}
                  domain="sculptureEdition"
                />
              ),
            },
            {
              accessorKey: "sculptureEditionTechniquePreview",
              header: t("sculpture.form.sculptureEditionTechnique"),
              cell: ({ row }: { row: Row<SculptureEditionInBibliography> }) => {
                const se = row.original.sculptureEdition;
                if (!se) return null;
                return (
                  <TextCell
                    value={pickSculptureEditionTechniqueLine(se, {
                      language: i18n.language as SupportedLocale,
                    })}
                  />
                );
              },
            },
            {
              accessorKey: "sculptureEditionDateRealisation",
              header: t("sculpture.form.sculptureEditionDateRealisation"),
              cell: ({ row }: { row: Row<SculptureEditionInBibliography> }) => (
                <TextCell
                  value={
                    row.original.sculptureEdition?.sculptureEditionDateRealisation ??
                    ""
                  }
                />
              ),
            },
            {
              accessorKey: "sculptureEditionNotes",
              header: t("sculpture.form.sculptureEditionNotes"),
              cell: ({ row }: { row: Row<SculptureEditionInBibliography> }) => (
                <HtmlCell
                  value={
                    row.original.sculptureEdition?.sculptureEditionNotes ?? ""
                  }
                />
              ),
            }
          ]
        : [
            {
              accessorKey: "bibliographieDescriptionVignette",
              header: "",
              size: 100,
              cell: ({ row }: { row: Row<SculptureEditionInBibliography> }) => {
                return (
                  <ImageCell
                    value={""}
                    attachments={row.original.bibliography?.attachments ?? []}
                    domain={"bibliography"}
                  />
                );
              },
            },
            {
              accessorKey: "bibliographieDescriptionAnnee",
              header: t("bibliographieDescriptionAnnee"),
              cell: ({ row }: { row: Row<SculptureEditionInBibliography> }) => (
                <TextCell
                  value={row.original.bibliography?.bibliographieDescriptionAnnee ?? ""}
                />
              ),
            },
            {
              accessorKey: "bibliographieDescriptionAuteur",
              header: t("bibliographieDescriptionAuteur"),
              cell: ({ row }: { row: Row<SculptureEditionInBibliography> }) => (
                <TextCell
                  value={
                    row.original.bibliography?.bibliographieDescriptionAuteur ?? ""
                  }
                />
              ),
            },
          ]),
      {
        accessorKey: "sculptureEditionDansBibliographieReproduitPage",
        header: t("bibliographieDansEstampesMultiplesDescriptionReproduitPage"),
        cell: ({ row }: { row: Row<SculptureEditionInBibliography> }) => (
          <TextCell
            value={row.original.sculptureEditionDansBibliographieReproduitPage ?? ""}
          />
        ),
      },
      {
        accessorKey: "sculptureEditionDansBibliographieCitePage",
        header: t("bibliographieDansEstampesMultiplesDescriptionCitePage"),
        cell: ({ row }: { row: Row<SculptureEditionInBibliography> }) => (
          <TextCell
            value={row.original.sculptureEditionDansBibliographieCitePage ?? ""}
          />
        ),
      },
      {
        accessorKey: "sculptureEditionDansBibliographiePhotographe",
        header: t("bibliographieDansEstampesMultiplesDescriptionPhotographe"),
        cell: ({ row }: { row: Row<SculptureEditionInBibliography> }) => (
          <TextCell
            value={row.original.sculptureEditionDansBibliographiePhotographe ?? ""}
          />
        ),
      },
      {
        accessorKey: "sculptureEditionDansBibliographieReproductionCouleur",
        header: t(
          "bibliographieDansEstampesMultiplesDescriptionReproductionCoul",
        ),
        cell: ({ row }: { row: Row<SculptureEditionInBibliography> }) => (
          <TextCell
            value={
              row.original.sculptureEditionDansBibliographieReproductionCouleur
                ? t("yes")
                : t("no")
            }
          />
        ),
      },
      {
        accessorKey: "sculptureEditionDansBibliographieReproduitPlanche",
        header: t(
          "bibliographieDansEstampesMultiplesDescriptionExemplaireRepr",
        ),
        cell: ({ row }: { row: Row<SculptureEditionInBibliography> }) => (
          <TextCell
            value={
              row.original.sculptureEditionDansBibliographieReproduitPlanche ??
              ""
            }
          />
        ),
      },
      createActionsColumnDef<SculptureEditionInBibliography>(({ row }) =>
        renderSculptureEditionInBibliographyRowActions(row.original),
      ),
    ],
    [fields, handleDeleteRelation, id, t, type],
  );

  return (
    <div className="w-full">
      {type === "bibliography" ? (
        <BibliographyToSculptureEditionLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          bibliography={bibliography}
          isAddMode={isAddMode}
          columnsLink={columns}
          renderRowActions={renderSculptureEditionInBibliographyRowActions}
          searchContent={searchContent}
          searchInput={
            <div className="flex items-center gap-2">
              <Input
                placeholder={t("search")}
                className="w-80"
                value={searchContent}
                onChange={(e) => setSearchContent(e.target.value)}
              />
            </div>
          }
          listGridTitle={t("linkedSculptureEditions")}
          layoutBackLabel={t("relatedRecords")}
          setIsAddMode={setIsAddMode}
        />
      ) : (
        <SculptureEditionToBibliographyLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          sculptureEdition={sculptureEdition}
          isAddMode={isAddMode}
          columnsLink={columns}
          renderRowActions={renderSculptureEditionInBibliographyRowActions}
          searchContent={searchContent}
          searchInput={
            <div className="flex items-center gap-2">
              <Input
                placeholder={t("search")}
                className="w-80"
                value={searchContent}
                onChange={(e) => setSearchContent(e.target.value)}
              />
            </div>
          }
          listGridTitle={t("linkedBibliographies")}
          layoutBackLabel={t("relatedRecords")}
          setIsAddMode={setIsAddMode}
        />
      )}
    </div>
  );
};

type BibliographyToSculptureEditionLinksProps =
  LinksTableProps<SculptureEditionInBibliography> & {
    bibliography: Bibliography | undefined;
  };

type SculptureEditionToBibliographyLinksProps =
  LinksTableProps<SculptureEditionInBibliography> & {
    sculptureEdition: SculptureEdition | undefined;
  };

/** Vue depuis une bibliographie : liste des éditions de sculpture liées à cette biblio */
const BibliographyToSculptureEditionLinks = ({
  id,
  relatedData,
  fields,
  bibliography,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: BibliographyToSculptureEditionLinksProps) => {
  const { t } = useTranslation();
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  useEffect(() => {
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD = "bibliography-links-sculpture-edition-add";
  const STORAGE_KEY_LINKED = "bibliography-links-sculpture-edition-linked";
  const defaultVisibilityAdd = { ...sculptureEditionsColumnsVisibility, actions: true };
  const defaultVisibilityLinked = { ...bibliographyColumnsVisibility, actions: true };
  const [columnVisibilityAdd, setColumnVisibilityAdd] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityAdd, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityAdd };
    });
  const [columnVisibilityLinked, setColumnVisibilityLinked] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityLinked, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityLinked };
    });
  const [columnsOrderAdd, setColumnsOrderAdd] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD);
      if (saved) {
        const { order } = saved;
        return order || [];
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinked, setColumnsOrderLinked] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_LINKED);
      if (saved) {
        const { order } = saved;
        return order || [];
      }
    } catch {
      // ignore
    }
    return [];
  });

  const { data: notRelatedEditions } = useGetNotRelatedResource<SculptureEdition>({
    resultType: "sculptureEdition",
    resourceId: id,
    pagination: getPagination,
    searchQuery: searchContent,
  });

  const columnsEditions: ColumnDef<SculptureEdition, any>[] = useMemo(
    () => [
      {
        accessorKey: "sculptureEditionNumeroEnregistrement",
        header: t("sculpture.form.sculptureEditionNumeroEnregistrement"),
        cell: ({ row }: { row: Row<SculptureEdition> }) => (
          <TextCell
            value={row.original.sculptureEditionNumeroEnregistrement ?? ""}
          />
        ),
      },
      {
        accessorKey: "vignette",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<SculptureEdition> }) => (
          <ImageCell
            value=""
            attachments={row.original.attachments ?? []}
            domain="sculptureEdition"
          />
        ),
      },
      {
        accessorKey: "sculptureEditionTechniquePreview",
        header: t("sculpture.form.sculptureEditionTechnique"),
        cell: ({ row }: { row: Row<SculptureEdition> }) => (
          <TextCell
            value={pickSculptureEditionTechniqueLine(row.original, {
              language: i18n.language as SupportedLocale,
            })}
          />
        ),
      },
      createActionsColumnDef<SculptureEdition>(({ row }: { row: Row<SculptureEdition> }) => {
          const data = row.original as SculptureEdition;
          if (!bibliography) return null;
          return (
            <LinkDrawerRowActions
              previewType="sculptureEdition"
              previewId={data.id}
              previewElements={[
                {
                  label: t("sculpture.form.sculptureEditionNumeroEnregistrement"),
                  value: data.sculptureEditionNumeroEnregistrement ?? "",
                  type: "text",
                },
                {
                  label: t("sculpture.form.sculptureEditionNotes"),
                  value: data.sculptureEditionNotes ?? "",
                  type: "richtext",
                },
                {
                  label: t("attachments"),
                  value: data.attachments ?? [],
                  type: "image",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <SculptureEditionInBibliographyDrawer
                  key={data.id}
                  fields={fields}
                  queryId={id}
                  type="bibliography"
                  open={open}
                  onOpenChange={onOpenChange}
                  data={{
                    id: "",
                    bibliography,
                    sculptureEdition: data,
                    sculptureEditionDansBibliographieReproduitPage: "",
                    sculptureEditionDansBibliographieReproduitPlanche: "",
                    sculptureEditionDansBibliographiePlancheNonNumerotee: false,
                    sculptureEditionDansBibliographieCitePage: "",
                    sculptureEditionDansBibliographieListePage: "",
                    sculptureEditionDansBibliographieSousNumero: "",
                    sculptureEditionDansBibliographiePhotographe: "",
                    sculptureEditionDansBibliographieNonPagine: false,
                    sculptureEditionDansBibliographieReproductionCouverture: false,
                    sculptureEditionDansBibliographieReproductionCouleur: false,
                    sculptureEditionDansBibliographieTexteLibre: "",
                    sculptureEditionDansBibliographieNotes: "",
                  }}
                />
              )}
            />
          );
        }),
    ],
    [bibliography, fields, id, t],
  );

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: (relatedData ?? []).length,
  };
  const addModeTable = (
    <DataTable
      columns={columnsEditions}
      data={notRelatedEditions?.data ?? []}
      pagination={{
        pagination: getPagination,
        setPage: setPagination,
        totalSize: notRelatedEditions?.totalSize ?? 0,
      }}
      storageKey={STORAGE_KEY_ADD}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibilityAdd,
        onColumnOrderChange: setColumnsOrderAdd,
        state: {
          columnVisibility: columnVisibilityAdd,
          columnOrder: columnsOrderAdd,
        },
      }}
    />
  );
  const listTable = (
    <DataTable
      columns={columnsLink}
      data={paginatedRelatedData}
      pagination={relatedPaginationProps}
      storageKey={STORAGE_KEY_LINKED}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibilityLinked,
        onColumnOrderChange: setColumnsOrderLinked,
        state: {
          columnVisibility: columnVisibilityLinked,
          columnOrder: columnsOrderLinked,
        },
      }}
    />
  );
  if (listGridTitle && setIsAddMode && searchInput) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <SculptureEditionLinkGridCard
                  item={row.sculptureEdition}
                  t={t}
                  onTextClick={() => setSelectedRelationId(row.id)}
                />
                </GridCardWithActions>
                                <SculptureEditionInBibliographyDrawer
                  fields={fields}
                  data={row}
                  queryId={id}
                  type="bibliography"
                  open={selectedRelationId === row.id}
                  onOpenChange={(open) => {
                    if (!open) {
                      setSelectedRelationId(null);
                    }
                  }}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }
  return isAddMode ? addModeTable : listTable;
};

//Composant pour Oeuvres
const SculptureEditionToBibliographyLinks = ({
  id,
  relatedData,
  fields,
  sculptureEdition,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: SculptureEditionToBibliographyLinksProps) => {
  const { t } = useTranslation();
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  useEffect(() => {
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD = "sculpture-edition-links-bibliography-add";
  const STORAGE_KEY_LINKED = "sculpture-edition-links-bibliography-linked";
  const defaultVisibility = { ...bibliographyColumnsVisibility, actions: true };
  const [columnVisibilityAdd, setColumnVisibilityAdd] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    });
  const [columnVisibilityLinked, setColumnVisibilityLinked] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    });
  const [columnsOrderAdd, setColumnsOrderAdd] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD);
      if (saved) {
        const { order } = saved;
        return order || [];
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinked, setColumnsOrderLinked] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_LINKED);
      if (saved) {
        const { order } = saved;
        return order || [];
      }
    } catch {
      // ignore
    }
    return [];
  });

  const { data: notRelatedBibliographies } =
    useGetNotRelatedResource<Bibliography>({
      resultType: "bibliography",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    });

  const columnsBibliographies: ColumnDef<Bibliography, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "bibliographieDescriptionNumeroBibliographie",
        header: t("bibliographieDescriptionNumeroBibliographie"),
        enableResizing: true,
        cell: ({ row }) => {
          return (
            <div className="flex items-center gap-2 text-nowrap">
              <p className="text-[14px] font-[500]">
                {row.original.bibliographieDescriptionNumeroBibliographie}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "bibliographieDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }) => {
          return (
            <ImageCell
              value={""}
              attachments={row.original.attachments}
              domain={"bibliography"}
            />
          );
        },
      },
      {
        accessorKey: "bibliographieDescriptionAnnee",
        header: t("bibliographieDescriptionAnnee"),
        size: 100,
        cell: ({ row }) => {
          return (
            <div className="flex items-center gap-3 text-nowrap">
              <p className="text-[14px] font-[500]">
                {row.original.bibliographieDescriptionAnnee}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "relatedAuthor",
        header: t("bibliographieDescriptionAuteur"),
        size: 200,
        cell: ({ row }) => {
          return (
            <div className="flex items-center gap-3 text-wrap">
              <p className="text-[14px] font-[500]">
                {row.original.relatedAuthor
                  .map((author: Author) => author.artistesAuteursDescriptionNom)
                  .join(", ")}
              </p>
            </div>
          );
        },
      },
      createActionsColumnDef<Bibliography>(({ row }) => {
          const data = row.original as Bibliography;
          if (!sculptureEdition) return null;
          return (
            <LinkDrawerRowActions
              previewType="bibliography"
              previewId={row.original.id}
              previewElements={[
                {
                  label: t("bibliographieDescriptionNumeroBibliographie"),
                  value: data.bibliographieDescriptionNumeroBibliographie,
                  type: "text",
                },
                {
                  label: t("bibliographieDescriptionResumeLong"),
                  value: data.bibliographieDescriptionResumeLong,
                  type: "richtext",
                },
                {
                  label: t("attachments"),
                  value: data.attachments,
                  type: "image",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <SculptureEditionInBibliographyDrawer
                  key={row.original.id}
                  fields={fields}
                  queryId={id}
                  type="sculptureEdition"
                  open={open}
                  onOpenChange={onOpenChange}
                  data={{
                    id: "",
                    bibliography: data,
                    sculptureEdition: sculptureEdition!,
                    sculptureEditionDansBibliographieReproduitPage: "",
                    sculptureEditionDansBibliographieReproduitPlanche: "",
                    sculptureEditionDansBibliographiePlancheNonNumerotee: false,
                    sculptureEditionDansBibliographieCitePage: "",
                    sculptureEditionDansBibliographieListePage: "",
                    sculptureEditionDansBibliographieSousNumero: "",
                    sculptureEditionDansBibliographiePhotographe: "",
                    sculptureEditionDansBibliographieNonPagine: false,
                    sculptureEditionDansBibliographieReproductionCouverture: false,
                    sculptureEditionDansBibliographieReproductionCouleur: false,
                    sculptureEditionDansBibliographieTexteLibre: "",
                    sculptureEditionDansBibliographieNotes: "",
                  }}
                />
              )}
            />
          );
        }),
    ];
  }, [fields, id, sculptureEdition, t]);

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: (relatedData ?? []).length,
  };
  const addModeTable = (
    <DataTable
      columns={columnsBibliographies}
      data={notRelatedBibliographies?.data ?? []}
      pagination={{
        pagination: getPagination,
        setPage: setPagination,
        totalSize: notRelatedBibliographies?.totalSize ?? 0,
      }}
      storageKey={STORAGE_KEY_ADD}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibilityAdd,
        onColumnOrderChange: setColumnsOrderAdd,
        state: {
          columnVisibility: columnVisibilityAdd,
          columnOrder: columnsOrderAdd,
        },
      }}
    />
  );
  const listTable = (
    <DataTable
      columns={columnsLink}
      data={paginatedRelatedData}
      pagination={relatedPaginationProps}
      storageKey={STORAGE_KEY_LINKED}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibilityLinked,
        onColumnOrderChange: setColumnsOrderLinked,
        state: {
          columnVisibility: columnVisibilityLinked,
          columnOrder: columnsOrderLinked,
        },
      }}
    />
  );
  if (listGridTitle && setIsAddMode && searchInput) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <BibliographyLinkGridCard
                  item={row.bibliography}
                  t={t}
                  onTextClick={() => setSelectedRelationId(row.id)}
                />
                </GridCardWithActions>
                                <SculptureEditionInBibliographyDrawer
                  fields={fields}
                  data={row}
                  queryId={id}
                  type="sculptureEdition"
                  open={selectedRelationId === row.id}
                  onOpenChange={(open: boolean) => {
                    if (!open) {
                      setSelectedRelationId(null);
                    }
                  }}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }
  return isAddMode ? addModeTable : listTable;
};

export default SculptureEditionInBibliographyLinks;
